import React, {useState} from 'react';
import * as Yup from "yup";
import {Formik, Form} from "formik";

import {useNavigate} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";

import FormInput from "../form-elements/FormInput";
import FormFieldset from "../form-elements/FormFieldset";
import FormInputPasswordShowHide from "../form-elements/FormInputPasswordShowHide";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import Button from "../common/Button";
import CustomLink from "../common/CustomLink";

const LoginFormSchema = Yup.object().shape({
  email: Yup.string().email('Please enter valid email').required('Please enter email'),
  password: Yup.string().required("Please enter password"),
})

interface LoginFormProps {
  prefilledEmail?: string;
}

function LoginForm({ prefilledEmail = '' }: LoginFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (
    values: { email: string; password: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const result = await login(values.email, values.password);
      
      console.log("LoginForm received result:", result);
      
      // Check if 2FA is required
      if (result.requires2FA && result.userId) {
        console.log("Redirecting to 2FA verification");
        // Redirect to 2FA verification page with the user ID
        navigate('/verify-2fa', { state: { userId: result.userId } });
        return;
      }
      
      // Check if user needs onboarding
      if (result.needsOnboarding) {
        console.log("Redirecting to onboarding");
        setSuccess("Login successful! Setting up your account...");
        navigate('/onboarding');
        return;
      }
      
      // Normal login flow (user has completed onboarding)
      console.log("Redirecting to credit lines");
      setSuccess("Login successful! Redirecting...");
      navigate('/'); // Redirect to credit lines
    } catch (err: any) {
      let errorMessage = "An error occurred during login.";
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      if (err.response) {
        const status = err.response.status;
        
        // Specific error messages by status code
        if (status === 403) {
          errorMessage = "Forbidden. The server does not allow this request.";
        } else if (status === 401) {
          errorMessage = "Unauthorized. Check your credentials.";
        } else if (status === 400) {
          errorMessage = "Invalid request. Check the information entered.";
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        email: prefilledEmail,
        password: ""
      }}
      validateOnMount={true}
      validateOnChange={true}
      validationSchema={LoginFormSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, errors, isValid, touched, dirty }) => (
        <Form
          className="c-form"
          noValidate
        >
          <FormFieldset
            extraClassName="authentication"
            legendText='Enter email and password'
          >

            <FormInput
              extraClassName="boxed"
              formGroupStyle="block-line"
              labelText="Email"
              type="email"
              name="email"
              placeholder="Enter email"
              autoComplete="email"
              hideError
            />

            <FormInputPasswordShowHide
              extraClassName="boxed"
              formGroupStyle="block-line"
              labelText="Password"
              name="password"
              placeholder="Enter password"
              hideError
            />

            <CustomLink
              extraClassName="c-authenticate-form-link"
              url="/reset-password"
              text="Forgot password?"
              linkStyle="reverse"
            />

            {errors.email && touched.email && (
              <FormGroupErrorText text={errors.email}/>
            )}

            {errors.password && touched.password && (
              <FormGroupErrorText text={errors.password}/>
            )}

            {error && <FormGroupErrorText text={error}/>}
            {success && <div className="success-message">{success}</div>}

          </FormFieldset>

          <Button
            type="submit"
            btnSize="lg"
            disabled={!isValid || isSubmitting || !dirty}
            isLoading={isLoading}
            isLoadingText="Logging in"
            text="Continue"
          />

        </Form>
      )}
    </Formik>
  );
}

export default LoginForm;